import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import Login from '../auth/Login';
import AdminNavbar from './AdminNavbar';
import HeroAdmin from './sections/HeroAdmin';
import AboutAdmin from './sections/AboutAdmin';
import PhotographyAdmin from './sections/PhotographyAdmin';
import YogaAdmin from './sections/YogaAdmin';
import ArtAdmin from './sections/ArtAdmin';

const AdminPanel = ({ siteData, setSiteData, updateSiteData, refreshCollectionData }) => {
    const [activeSection, setActiveSection] = useState('hero');
    const [saveStatus, setSaveStatus] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Check authentication status
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const showSaveStatus = (message, isError = false) => {
        setSaveStatus({ message, isError });
        setTimeout(() => setSaveStatus(null), 3000);
    };

    const renderSection = () => {
        const commonProps = {
            siteData,
            setSiteData,
            updateSiteData,
            refreshCollectionData,
            showSaveStatus
        };

        switch (activeSection) {
            case 'hero':
                return <HeroAdmin {...commonProps} />;
            case 'about':
                return <AboutAdmin {...commonProps} />;
            case 'photography':
                return <PhotographyAdmin {...commonProps} />;
            case 'yoga':
                return <YogaAdmin {...commonProps} />;
            case 'art':
                return <ArtAdmin {...commonProps} />;
            default:
                return <HeroAdmin {...commonProps} />;
        }
    };

    // Show loading spinner while checking auth
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Show login form if not authenticated
    if (!user) {
        return <Login onLoginSuccess={() => setUser(auth.currentUser)} />;
    }

    // Show admin panel if authenticated
    return (
        <div className="min-h-screen bg-gray-100">
            <AdminNavbar
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                user={user}
                onLogout={handleLogout}
            />

            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                {saveStatus && (
                    <div className={`mb-6 p-4 rounded-lg transition-all duration-300 ${saveStatus.isError
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-green-100 text-green-700 border border-green-200'
                        }`}>
                        <div className="flex items-center">
                            <span className="mr-2">
                                {saveStatus.isError ? '❌' : '✅'}
                            </span>
                            {saveStatus.message}
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-lg p-8">
                    {renderSection()}
                </div>
            </div>
        </div>
    );
};

export default AdminPanel;